import { Injectable, inject } from '@angular/core';
import { Subscription, timer } from 'rxjs';
import {
	IScanFaceAnimations,
	ILifeTest,
	ILifeTestGeneralConf,
	IMiddleFace,
	LIFE_TEST_ID,
} from 'src/app/user-identification/models/life-check';
import { TestStatus } from 'src/app/user-identification/models/user-identification';
import { PositionFaceService } from 'src/app/user-identification/services/pol/position-face.service';
import { ScoringFaceService } from 'src/app/user-identification/services/pol/scoring-face.service';
import { AudioService } from './audio.service';

@Injectable({
	providedIn: 'root',
})
export class AnimationsService {
	private readonly TIME_CHECK: number = 1000;
	private readonly TIME_ARROW: number = 2600;
	private readonly TURN_PROGRESS_BLEND_WEIGHT = 0.7;
	private readonly TURN_PROGRESS_DISTANCE_WEIGHT = 0.3;
	private subsArrow: Subscription;
	private scoringFaceService = inject(ScoringFaceService);
	private positionFaceService = inject(PositionFaceService);
	private audioService = inject(AudioService);

	private resolveTurnProgressValue(faceBlendshapesProgress: number, distanceProgress: number): number {
		const blend = Math.min(100, Math.max(0, faceBlendshapesProgress));
		const distance = Math.min(100, Math.max(0, distanceProgress));
		const weighted = blend * this.TURN_PROGRESS_BLEND_WEIGHT + distance * this.TURN_PROGRESS_DISTANCE_WEIGHT;
		// Sin desplazamiento de nariz no se completa la barra (ojos solos no bastan).
		return Math.min(100, Math.max(0, Math.min(weighted, distance)));
	}

	/*
	 * Animaciones
	 */
	public setAnimations(animations: IScanFaceAnimations, newAnimations: Partial<IScanFaceAnimations>): void {
		Object.assign(animations, newAnimations);
		if (animations.check) {
			animations.grow = false;
		}
		if (newAnimations.check) {
			this.audioService.playSuccessAudio();
			timer(this.TIME_CHECK).subscribe(() => ((animations.smile = false), (animations.check = false)));
		}
		if (newAnimations.halfRight !== null && newAnimations.halfRight !== undefined) {
			animations.halfRight = newAnimations.halfRight;
		}
		if (newAnimations.halfLeft !== null && newAnimations.halfLeft !== undefined) {
			animations.halfLeft = newAnimations.halfLeft;
		}
		if (newAnimations.showArrow) {
			// Siempre reseteamos el timer para que la flecha se muestre de forma continua
			if (this.subsArrow) {
				this.subsArrow.unsubscribe();
				this.subsArrow = null;
			}
			this.subsArrow = new Subscription();
			this.subsArrow.add(
				timer(this.TIME_ARROW).subscribe(() => {
					animations.showArrow = false;
					this.subsArrow.unsubscribe();
					this.subsArrow = null;
				}),
			);
		} else {
			if (this.subsArrow) {
				this.subsArrow.unsubscribe();
				this.subsArrow = null;
			}
		}
	}

	public animationArrowByTest(
		lifeTest: ILifeTest,
		faceBlendshapes: any,
		scanFaceAnimation: IScanFaceAnimations,
	): void {
		if (lifeTest.status !== TestStatus.OnComplete) {
			const allLifeTest = {
				[LIFE_TEST_ID.TURNLEFTHEAD]: () =>
					this.animationArrow(lifeTest, faceBlendshapes, scanFaceAnimation, 'left'),
				[LIFE_TEST_ID.TURNRIGHTHEAD]: () =>
					this.animationArrow(lifeTest, faceBlendshapes, scanFaceAnimation, 'right'),
				[LIFE_TEST_ID.LIFTHEAD]: () => this.animationArrow(lifeTest, faceBlendshapes, scanFaceAnimation, 'top'),
				[LIFE_TEST_ID.LOWERHEAD]: () =>
					this.animationArrow(lifeTest, faceBlendshapes, scanFaceAnimation, 'bottom'),
			};
			allLifeTest[lifeTest.lifeTestId] ? allLifeTest[lifeTest.lifeTestId]() : null;
		}
	}

	private animationArrow(
		lifeTest: ILifeTest,
		faceBlendshapes: any,
		scanFaceAnimation: IScanFaceAnimations,
		positionArrow: 'left' | 'right' | 'bottom' | 'top',
	): void {
		const nosePoint = this.positionFaceService.getNosePoint();
		const noseCenterPoint = this.positionFaceService.getNoseCenterPoint();
		if (!nosePoint || !noseCenterPoint) {
			return;
		}
		const noseDistance =
			positionArrow === 'left' || positionArrow === 'right'
				? Math.abs(nosePoint.x - noseCenterPoint.x)
				: Math.abs(nosePoint.y - noseCenterPoint.y);
		const shouldShow =
			lifeTest.minPercentageFaceBlendshape / 2 > this.scoringFaceService.getAverageArray([faceBlendshapes])[0] &&
			lifeTest.noseMovement.minDistance / 2 > noseDistance;
		if (shouldShow) {
			this.setAnimations(scanFaceAnimation, {
				showArrow: true,
				positionArrow: positionArrow,
			});
		}
	}

	public growAnimationByTest(
		scanFaceAnimation: IScanFaceAnimations,
		lifeTest: ILifeTest,
		faceBlendshapes: any,
		general?: ILifeTestGeneralConf,
		validationStartedAtMs?: number | null,
	): void {
		const allLifeTest = {
			[LIFE_TEST_ID.TURNLEFTHEAD]: () => {
				if (scanFaceAnimation.halfLeft) {
					timer(500).subscribe(() => {
						this.setAnimations(scanFaceAnimation, {
							grow: true,
							halfLeft: true,
							halfRight: false,
							halfBottom: false,
							halfTop: false,
							smile: false,
						});
					});
				} else {
					this.setAnimations(scanFaceAnimation, {
						grow: false,
						halfLeft: true,
						halfRight: false,
						halfBottom: false,
						halfTop: false,
						smile: false,
					});
				}
				if (!scanFaceAnimation.grow) {
					return;
				}
				const faceBlendshapesProgress: number =
					Math.round(
						Math.floor(
							(this.scoringFaceService.getAverageArray([faceBlendshapes])[0] * 100) /
								lifeTest.minPercentageFaceBlendshape,
						) / 10,
					) * 10;
				const distanceProgress: number =
					Math.round(
						Math.floor(
							((this.positionFaceService.getNosePoint().x -
								this.positionFaceService.getNoseCenterPoint().x) *
								100) /
								lifeTest.noseMovement.minDistance,
						) / 10,
					) * 10;
				scanFaceAnimation.progressValue = this.resolveTurnProgressValue(
					faceBlendshapesProgress,
					distanceProgress,
				);
			},
			[LIFE_TEST_ID.TURNRIGHTHEAD]: () => {
				if (scanFaceAnimation.halfRight) {
					timer(500).subscribe(() => {
						this.setAnimations(scanFaceAnimation, {
							grow: true,
							halfLeft: false,
							halfRight: true,
							halfBottom: false,
							halfTop: false,
							smile: false,
						});
					});
				} else {
					this.setAnimations(scanFaceAnimation, {
						grow: false,
						halfLeft: false,
						halfRight: true,
						halfBottom: false,
						halfTop: false,
						smile: false,
					});
				}
				if (!scanFaceAnimation.grow) {
					return;
				}
				const faceBlendshapesProgress: number =
					Math.round(
						Math.floor(
							(this.scoringFaceService.getAverageArray([faceBlendshapes])[0] * 100) /
								lifeTest.minPercentageFaceBlendshape,
						) / 10,
					) * 10;
				const distanceProgress: number =
					Math.round(
						Math.floor(
							((this.positionFaceService.getNoseCenterPoint().x -
								this.positionFaceService.getNosePoint().x) *
								100) /
								lifeTest.noseMovement.minDistance,
						) / 10,
					) * 10;
				scanFaceAnimation.progressValue = this.resolveTurnProgressValue(
					faceBlendshapesProgress,
					distanceProgress,
				);
			},
			[LIFE_TEST_ID.SMILE]: () => {
				const progressValue =
					general != null
						? this.scoringFaceService.getLifeTestValidationProgress(
								lifeTest,
								general,
								validationStartedAtMs ?? null,
							)
						: 0;
				const smileIntensity = this.scoringFaceService.getAverageArray([faceBlendshapes])[0] ?? 0;
				this.setAnimations(scanFaceAnimation, {
					grow: smileIntensity > lifeTest.minPercentageFaceBlendshape / 2,
					halfLeft: false,
					halfRight: false,
					halfBottom: false,
					halfTop: false,
					smile: true,
				});
				scanFaceAnimation.progressValue = progressValue;
			},
			[LIFE_TEST_ID.LIFTHEAD]: () => {
				if (scanFaceAnimation.halfTop) {
					timer(500).subscribe(() => {
						this.setAnimations(scanFaceAnimation, {
							grow: true,
							halfLeft: false,
							halfRight: false,
							halfBottom: false,
							halfTop: true,
							smile: false,
						});
					});
				} else {
					this.setAnimations(scanFaceAnimation, {
						grow: false,
						halfLeft: false,
						halfRight: false,
						halfBottom: false,
						halfTop: true,
						smile: false,
					});
				}
				if (!scanFaceAnimation.grow) {
					return;
				}
				const validationProgress =
					general != null
						? this.scoringFaceService.getLifeTestValidationProgress(
								lifeTest,
								general,
								validationStartedAtMs ?? null,
							)
						: 0;
				let progressValue: number = 0;
				const faceBlendshapesProgress: number =
					Math.round(
						Math.floor(
							(this.scoringFaceService.getAverageArray([faceBlendshapes])[0] * 100) /
								lifeTest.minPercentageFaceBlendshape,
						) / 10,
					) * 10;
				const distanceProgress: number =
					Math.round(
						Math.floor(
							((this.positionFaceService.getNoseCenterPoint().y -
								this.positionFaceService.getNosePoint().y) *
								100) /
								lifeTest.noseMovement.minDistance,
						) / 10,
					) * 10;
				progressValue = faceBlendshapesProgress * 0.4 + distanceProgress * 0.6;
				if (progressValue < 0) {
					scanFaceAnimation.progressValue = 0;
				} else if (distanceProgress < 11) {
					// Evita reinicio brusco por jitter cerca del umbral de distancia.
					scanFaceAnimation.progressValue = Math.max(0, scanFaceAnimation.progressValue - 5);
				} else if (progressValue >= 0 && progressValue <= 100) {
					scanFaceAnimation.progressValue =
						general != null ? Math.min(progressValue, validationProgress) : progressValue;
				} else {
					scanFaceAnimation.progressValue = general != null ? Math.min(100, validationProgress) : 100;
				}
			},
			[LIFE_TEST_ID.LOWERHEAD]: () => {
				if (scanFaceAnimation.halfBottom) {
					timer(500).subscribe(() => {
						this.setAnimations(scanFaceAnimation, {
							grow: true,
							halfLeft: false,
							halfRight: false,
							halfBottom: true,
							halfTop: false,
							smile: false,
						});
					});
				} else {
					this.setAnimations(scanFaceAnimation, {
						grow: false,
						halfLeft: false,
						halfRight: false,
						halfBottom: true,
						halfTop: false,
						smile: false,
					});
				}
				if (!scanFaceAnimation.grow) {
					return;
				}
				let progressValue: number = 0;
				const faceBlendshapesProgress: number =
					Math.round(
						Math.floor(
							(this.scoringFaceService.getAverageArray([faceBlendshapes])[0] * 100) /
								(lifeTest.minPercentageFaceBlendshape + 10),
						) / 10,
					) * 10;
				const distanceProgress: number =
					Math.round(
						Math.floor(
							((this.positionFaceService.getNosePoint().y -
								this.positionFaceService.getNoseCenterPoint().y) *
								100) /
								(lifeTest.noseMovement.minDistance + 10),
						) / 10,
					) * 10;
				progressValue = (faceBlendshapesProgress + distanceProgress) / 2;
				if (progressValue < 0) {
					scanFaceAnimation.progressValue = 0;
				} else if (progressValue >= 0 && progressValue <= 100) {
					scanFaceAnimation.progressValue = progressValue;
				} else {
					scanFaceAnimation.progressValue = 100;
				}
			},
		};
		allLifeTest[lifeTest.lifeTestId]();
	}

	public checkAnimationByTest(scanFaceAnimation: IScanFaceAnimations, lifeTest: ILifeTest | IMiddleFace): void {
		const allLifeTest = {
			[LIFE_TEST_ID.MIDDLEFACE]: () => {
				this.setAnimations(scanFaceAnimation, {
					check: true,
					grow: false,
					halfLeft: false,
					halfRight: false,
					halfBottom: false,
					halfTop: false,
				});
				scanFaceAnimation.progressValue = 0;
			},
			[LIFE_TEST_ID.TURNLEFTHEAD]: () => {
				this.setAnimations(scanFaceAnimation, {
					check: true,
					grow: false,
					halfLeft: true,
					halfRight: false,
					halfBottom: false,
					halfTop: false,
				});
				scanFaceAnimation.progressValue = 0;
			},
			[LIFE_TEST_ID.TURNRIGHTHEAD]: () => {
				this.setAnimations(scanFaceAnimation, {
					check: true,
					grow: false,
					halfLeft: false,
					halfRight: true,
					halfBottom: false,
					halfTop: false,
				});
				scanFaceAnimation.progressValue = 0;
			},
			[LIFE_TEST_ID.SMILE]: () => {
				this.setAnimations(scanFaceAnimation, {
					check: true,
					grow: false,
					halfLeft: false,
					halfRight: false,
					halfBottom: false,
					halfTop: false,
				});
				scanFaceAnimation.progressValue = 0;
			},
			[LIFE_TEST_ID.APPROACHFACE]: () => {
				this.setAnimations(scanFaceAnimation, {
					check: true,
					grow: false,
					halfLeft: false,
					halfRight: false,
					halfBottom: false,
					halfTop: false,
				});
				scanFaceAnimation.progressValue = 0;
			},
			[LIFE_TEST_ID.LIFTHEAD]: () => {
				this.setAnimations(scanFaceAnimation, {
					check: true,
					grow: false,
					halfLeft: false,
					halfRight: false,
					halfBottom: false,
					halfTop: true,
				});
				scanFaceAnimation.progressValue = 0;
			},
			[LIFE_TEST_ID.LOWERHEAD]: () => {
				this.setAnimations(scanFaceAnimation, {
					check: true,
					grow: false,
					halfLeft: false,
					halfRight: false,
					halfBottom: true,
					halfTop: false,
				});
				scanFaceAnimation.progressValue = 0;
			},
		};
		allLifeTest[lifeTest.lifeTestId]();
	}
}
